/**
 * AdjustShiftTimeDialog — reusable dialog for the "Admin Shift Time
 * Adjustment" feature. Powers three scopes:
 *   - single shift (`shift_ids: [id]`)
 *   - full event day (`open_shift_id + date`)
 *   - subset of employees on the day (`open_shift_id + date + employee_ids`)
 *
 * The reason field is mandatory. On save, the parent's `onSaved()` callback
 * fires so the calling page can refresh its data.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Clock, AlertTriangle, Users, ClipboardList } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Props:
 *  open, onOpenChange   — dialog state
 *  scope                — 'shift' | 'day' | 'select'
 *  shift                — single shift row (when scope='shift')
 *  openShiftId + date   — event day identifier (when scope='day' or 'select')
 *  eventName            — human label for the dialog header
 *  candidates           — [{ id, employee_id, employee_name, start_time, end_time, status }]
 *                         list of shifts on that day, needed for scope='select'
 *  onSaved              — callback fired after a successful save
 */
export default function AdjustShiftTimeDialog({
  open,
  onOpenChange,
  scope,
  shift,
  openShiftId,
  date,
  eventName,
  candidates = [],
  onSaved,
}) {
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [reason, setReason] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  // Prefill with the shift's current times so admin only tweaks what's
  // wrong (matches the requirement's "adjust FROM 23:30 TO 23:00" wording).
  useEffect(() => {
    if (!open) return;
    if (scope === 'shift' && shift) {
      setNewStart(shift.start_time || '');
      setNewEnd(shift.end_time || '');
    } else if (scope === 'day' && candidates.length) {
      setNewStart(candidates[0].start_time || '');
      setNewEnd(candidates[0].end_time || '');
    } else {
      setNewStart('');
      setNewEnd('');
    }
    setReason('');
    setSelectedIds(new Set());
  }, [open, scope, shift, candidates]);

  const affectedCount = useMemo(() => {
    if (scope === 'shift') return 1;
    if (scope === 'day') return candidates.length;
    return selectedIds.size;
  }, [scope, candidates, selectedIds]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error('Please enter a reason (min 3 characters).');
      return;
    }
    if (!newStart && !newEnd) {
      toast.error('Change at least one of Start or End time.');
      return;
    }
    if (scope === 'select' && selectedIds.size === 0) {
      toast.error('Select at least one employee.');
      return;
    }
    // Build the API payload according to the scope so the backend can
    // resolve the correct shift set.
    const payload = { reason: reason.trim() };
    if (newStart) payload.new_start_time = newStart;
    if (newEnd) payload.new_end_time = newEnd;
    if (scope === 'shift') {
      payload.shift_ids = [shift.id];
    } else if (scope === 'day') {
      payload.open_shift_id = openShiftId;
      payload.date = date;
    } else {
      payload.open_shift_id = openShiftId;
      payload.date = date;
      payload.employee_ids = Array.from(selectedIds);
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/shifts/adjust-time`, payload);
      toast.success(`Adjusted ${res.data.adjusted_count} shift(s)`);
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save adjustment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!bg-card border-border max-w-md backdrop-blur"
        data-testid="adjust-time-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Adjust Actual Times
          </DialogTitle>
          <DialogDescription>
            Updates the scheduled times and moves the underlying clock-in / clock-out
            events so payroll matches reality. Original timestamps are preserved
            in the audit log.
          </DialogDescription>
        </DialogHeader>

        {/* Header context: which event/date we're editing */}
        <div className="text-xs text-muted-foreground bg-muted/40 rounded-sm px-3 py-2 space-y-0.5">
          {eventName && <div><span className="text-muted-foreground/60">Event:</span> {eventName}</div>}
          {date && <div><span className="text-muted-foreground/60">Date:</span> {date}</div>}
          <div className="flex items-center gap-1">
            <Users className="w-3 h-3" /> Affects <b className="text-foreground">{affectedCount}</b> employee(s)
          </div>
        </div>

        {/* Time inputs */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">New Start</Label>
            <Input
              type="time"
              value={newStart}
              onChange={(e) => setNewStart(e.target.value)}
              className="bg-background border-border rounded-sm"
              data-testid="adjust-start-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">New End</Label>
            <Input
              type="time"
              value={newEnd}
              onChange={(e) => setNewEnd(e.target.value)}
              className="bg-background border-border rounded-sm"
              data-testid="adjust-end-input"
            />
          </div>
        </div>

        {/* Employee selector for scope='select' */}
        {scope === 'select' && (
          <div className="border border-border rounded-sm max-h-48 overflow-y-auto p-1"
               data-testid="adjust-employee-list">
            {candidates.length === 0 ? (
              <p className="text-sm text-muted-foreground p-3">No employees on this shift.</p>
            ) : (
              candidates.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/40 rounded-sm cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(c.employee_id)}
                    onChange={() => toggle(c.employee_id)}
                    className="accent-primary"
                    data-testid={`adjust-emp-${c.employee_id}`}
                  />
                  <span className="flex-1">{c.employee_name}</span>
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {c.start_time}–{c.end_time}
                  </span>
                </label>
              ))
            )}
          </div>
        )}

        {/* Reason (mandatory) */}
        <div className="space-y-1">
          <Label htmlFor="adjust-reason" className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <ClipboardList className="w-3 h-3" /> Reason <span className="text-red-400">*</span>
          </Label>
          <textarea
            id="adjust-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Event actually finished at 23:00 instead of 23:30"
            rows={3}
            className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm resize-none"
            data-testid="adjust-reason-input"
            required
          />
        </div>

        {/* Warning banner reminding admin nothing is deleted */}
        <div className="flex items-start gap-2 p-2 rounded-sm bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          Clock-in / clock-out events are NOT deleted — only their timestamps
          are updated. The originals are stored on the audit log entry.
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="adjust-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !reason.trim() || (!newStart && !newEnd) || (scope === 'select' && selectedIds.size === 0)}
            data-testid="adjust-save"
            className={cn('rounded-sm')}
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Adjustment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
